import React, { createContext, useContext, useState, useMemo } from "react";
import about from "../translation/about.json";
import landing from "../translation/landing.json";
import resources from "../translation/resources.json";
import careers from "../translation/careers.json";
import helpBanner from "../translation/helpBanner.json";
import sales from "../translation/sales.json";
import privacyPolicy from "../translation/privacyPolicyTranslations.json";
import cookies from "../translation/cookiePolicyTranslations.json";
import carbon24 from "../translation/CarbonReduction2024.json";
import carbon26 from "../translation/CarbonReduction2026.json";

// Create context
const LanguageContext = createContext();

// Define custom hook to consume the language context 
export const useLanguage = () => useContext(LanguageContext);

// Define the set of available language codes
const supportedLanguages = ["en", "fr", "de", "es"];

// Function to resolve user's initial language preference
const getInitialLanguage = () => {
  const stored = localStorage.getItem("preferredLanguage");

  // If a preferred language was previously saved and is supported, use it
  if (stored && supportedLanguages.includes(stored)) return stored;

  // Otherwise, use browser
  const browserLang = navigator.language.slice(0, 2);
  return supportedLanguages.includes(browserLang) ? browserLang : "en";
};

/**
 * LanguageProvider wraps the app and makes the selected language
 * and translations accessible via context.
 */
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage());

  const handleLanguageChange = (lang) => {
    setCurrentLanguage(lang);
    localStorage.setItem("preferredLanguage", lang); // Make language persist for re-visiters
  };

  const allTranslations = {
    about,
    landing,
    careers,
    helpBanner,
    resources,
    sales,
    privacyPolicy,
    cookies,
    carbon24,
    carbon26
  };

  /**
   * Memoize the translations object based on the currently selected language. 
   * Prevents recalculating all translations on every re-render.
   * 
   * Loops through each translation namespace (e.g., "about", "careers", etc.)
   * Extracts only the translations for the active language
  */
  const translations = useMemo(() => {
    const result = {};
    for (const [namespace, data] of Object.entries(allTranslations)) {
      result[namespace] = data[currentLanguage] || {};
    }
    return result;
  }, [currentLanguage]);

  return (
    <LanguageContext.Provider
      value={{ currentLanguage, handleLanguageChange, translations }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
